<template>
  <header
    :class="[
      'fixed inset-x-0 top-0 z-50 transition-all duration-300',
      scrolled ? 'bg-white shadow-[0_1px_0_rgba(15,23,42,0.04)] backdrop-blur-xl' : 'bg-white',
    ]"
  >
    <nav class="marketing-shell flex h-16 items-center justify-between">
      <!-- Logo -->
      <RouterLink to="/" class="flex shrink-0 items-center gap-2.5">
        <div class="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600">
          <span class="text-xs font-bold text-white">贝</span>
        </div>
        <div>
          <p class="text-sm font-bold leading-none text-slate-900">贝易数据</p>
          <p class="text-[10px] leading-none text-slate-500 mt-0.5">Beiyi Data</p>
        </div>
      </RouterLink>

      <!-- Desktop navigation -->
      <div class="hidden items-center gap-1 min-[900px]:flex">
        <!-- 产品 dropdown -->
        <div class="relative" @mouseenter="activeMenu = 'products'" @mouseleave="activeMenu = null">
          <button
            type="button"
            class="flex items-center gap-1 rounded-lg px-4 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-50 hover:text-slate-900"
            :aria-expanded="activeMenu === 'products'"
            @click="activeMenu = 'products'"
            @keydown.esc="activeMenu = null"
          >
            产品
            <PhCaretDown :size="13" class="opacity-50 transition-transform" :class="activeMenu === 'products' ? 'rotate-180' : ''" aria-hidden="true" />
          </button>
          <transition
            enter-active-class="transition duration-150 ease-out"
            enter-from-class="opacity-0 translate-y-1"
            enter-to-class="opacity-100 translate-y-0"
            leave-active-class="transition duration-100 ease-in"
            leave-from-class="opacity-100 translate-y-0"
            leave-to-class="opacity-0 translate-y-1"
          >
            <div v-show="activeMenu === 'products'" class="absolute left-0 top-full mt-1 w-72 rounded-xl border border-slate-200 bg-white p-2 shadow-[0_16px_48px_rgba(15,23,42,0.10)]">
              <div class="mb-1 px-3 py-1.5">
                <p class="text-[10px] font-medium uppercase tracking-wider text-slate-500">核心产品</p>
              </div>
              <RouterLink
                v-for="p in navProducts"
                :key="p.id"
                :to="`/products/${p.id}`"
                class="flex items-center gap-3 rounded-lg px-3 py-2.5 transition-colors hover:bg-slate-50"
                @click="activeMenu = null"
              >
                <div class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg" :style="{ backgroundColor: p.color + '22' }">
                  <component :is="p.icon" :size="20" weight="duotone" :style="{ color: p.color }" aria-hidden="true" />
                </div>
                <div>
                  <p class="text-sm font-medium text-slate-900">{{ p.name }}</p>
                  <p class="text-xs text-slate-500">{{ p.tagline }}</p>
                </div>
              </RouterLink>
            </div>
          </transition>
        </div>

        <!-- 解决方案 dropdown -->
        <div class="relative" @mouseenter="activeMenu = 'solutions'" @mouseleave="activeMenu = null">
          <button
            type="button"
            class="flex items-center gap-1 rounded-lg px-4 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-50 hover:text-slate-900"
            :aria-expanded="activeMenu === 'solutions'"
            @click="activeMenu = 'solutions'"
            @keydown.esc="activeMenu = null"
          >
            解决方案
            <PhCaretDown :size="13" class="opacity-50 transition-transform" :class="activeMenu === 'solutions' ? 'rotate-180' : ''" aria-hidden="true" />
          </button>
          <transition
            enter-active-class="transition duration-150 ease-out"
            enter-from-class="opacity-0 translate-y-1"
            enter-to-class="opacity-100 translate-y-0"
            leave-active-class="transition duration-100 ease-in"
            leave-from-class="opacity-100 translate-y-0"
            leave-to-class="opacity-0 translate-y-1"
          >
            <div v-show="activeMenu === 'solutions'" class="absolute left-0 top-full mt-1 w-64 rounded-xl border border-slate-200 bg-white p-2 shadow-[0_16px_48px_rgba(15,23,42,0.10)]">
              <div class="mb-1 px-3 py-1.5">
                <p class="text-[10px] font-medium uppercase tracking-wider text-slate-500">行业解决方案</p>
              </div>
              <RouterLink
                v-for="s in navSolutions"
                :key="s.id"
                :to="`/solutions/${s.id}`"
                class="flex items-center gap-3 rounded-lg px-3 py-2.5 transition-colors hover:bg-slate-50"
                @click="activeMenu = null"
              >
                <component :is="s.icon" :size="20" weight="duotone" aria-hidden="true" />
                <p class="text-sm font-medium text-slate-900">{{ s.title }}</p>
              </RouterLink>
            </div>
          </transition>
        </div>

        <button
          type="button"
          class="rounded-lg px-4 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-50 hover:text-slate-900"
          @click="scrollTo('about')"
        >
          关于我们
        </button>

        <button
          type="button"
          class="rounded-lg px-4 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-50 hover:text-slate-900"
          @click="scrollTo('contact')"
        >
          联系我们
        </button>
      </div>

      <!-- Right side: lang + CTA -->
      <div class="hidden items-center gap-3 min-[900px]:flex">
        <div class="flex items-center gap-0.5 rounded-lg border border-slate-200 bg-white p-1">
          <button
            v-for="item in locales"
            :key="item"
            type="button"
            class="rounded-md px-2.5 py-1 text-xs transition-colors"
            :class="locale === item ? 'bg-slate-100 text-slate-900' : 'text-slate-500 hover:text-slate-700'"
            @click="setLocale(item)"
          >
            {{ localeNames[item] }}
          </button>
        </div>
        <a
          href="mailto:beibei@beiyidata.cn"
          class="rounded-lg bg-blue-600 px-5 py-2 text-sm font-medium text-white transition-all hover:bg-blue-500 hover:shadow-[0_0_20px_rgba(37,99,235,0.4)]"
        >
          申请演示
        </a>
      </div>

      <!-- Mobile menu button -->
      <button
        type="button"
        class="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-slate-200 text-slate-900 min-[900px]:hidden"
        @click="mobileOpen = !mobileOpen"
        :aria-expanded="mobileOpen"
        aria-label="菜单"
      >
        <PhList v-if="!mobileOpen" :size="20" /><PhX v-else :size="20" />
      </button>
    </nav>

    <!-- Mobile menu -->
    <transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0 -translate-y-2"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 -translate-y-2"
    >
      <div v-if="mobileOpen" class="border-t border-slate-200 bg-white px-5 pb-6 min-[900px]:hidden">
        <div class="mt-4 space-y-1">
          <p class="px-3 pt-2 text-[10px] font-medium uppercase tracking-wider text-slate-600">产品</p>
          <RouterLink
            v-for="p in navProducts"
            :key="p.id"
            :to="`/products/${p.id}`"
            class="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-slate-700"
            @click="mobileOpen = false"
          >
            <component :is="p.icon" :size="20" weight="duotone" aria-hidden="true" />
            {{ p.name }}
          </RouterLink>

          <p class="px-3 pt-3 text-[10px] font-medium uppercase tracking-wider text-slate-600">解决方案</p>
          <RouterLink
            v-for="s in navSolutions"
            :key="s.id"
            :to="`/solutions/${s.id}`"
            class="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-slate-700"
            @click="mobileOpen = false"
          >
            <component :is="s.icon" :size="20" weight="duotone" aria-hidden="true" />
            {{ s.title }}
          </RouterLink>

          <div class="mt-2 border-t border-slate-200 pt-3">
            <button class="block w-full rounded-lg px-3 py-2.5 text-left text-sm text-slate-700" @click="scrollTo('about')">关于我们</button>
            <button class="block w-full rounded-lg px-3 py-2.5 text-left text-sm text-slate-700" @click="scrollTo('contact')">联系我们</button>
          </div>

          <div class="mt-3 pt-2">
            <a href="mailto:beibei@beiyidata.cn" class="block w-full rounded-lg bg-blue-600 py-2.5 text-center text-sm font-medium text-white">
              申请演示
            </a>
          </div>
        </div>
      </div>
    </transition>
  </header>
</template>

<script setup>
import { onMounted, onUnmounted, ref } from 'vue'
import { PhCaretDown, PhList, PhX, PhDatabase, PhFileText, PhCube, PhBookOpen, PhUsers, PhBuildings, PhBank, PhLightning, PhFactory } from '@phosphor-icons/vue'
import { useRouter } from 'vue-router'
import { useI18n } from '../composables/useI18n'
import { visibleEnterpriseProducts } from '../data/products'
import { solutions } from '../data/solutions'

const { locale, locales, localeNames, setLocale } = useI18n()
const router = useRouter()

const activeMenu = ref(null)
const mobileOpen = ref(false)
const scrolled = ref(false)

const navProducts = visibleEnterpriseProducts.map((p) => ({
  id: p.id,
  name: typeof p.name === 'string' ? p.name : p.name.zh,
  tagline: typeof p.tagline === 'string' ? p.tagline : p.tagline.zh,
  icon: {
    'data-governance': PhDatabase,
    'document-processing': PhFileText,
    nexa: PhCube,
    'knowledge-base': PhBookOpen,
    cdp: PhUsers,
  }[p.id] || PhCube,
  color: p.accentColor,
}))

const navSolutions = solutions.map((s) => ({ id: s.id, title: s.title, icon: { enterprise: PhBank, government: PhBuildings, energy: PhLightning, manufacturing: PhFactory }[s.id] }))

const scrollTo = (id) => {
  mobileOpen.value = false
  activeMenu.value = null
  // If on homepage, scroll; otherwise navigate home then scroll
  if (router.currentRoute.value.path === '/') {
    const el = document.getElementById(id)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  } else {
    router.push({ path: '/', hash: `#${id}` })
  }
}

const handleScroll = () => { scrolled.value = window.scrollY > 20 }

onMounted(() => {
  handleScroll()
  window.addEventListener('scroll', handleScroll, { passive: true })
})
onUnmounted(() => window.removeEventListener('scroll', handleScroll))
</script>
